import { push } from 'connected-react-router';
import * as React from 'react';
import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { Export, UserProductGroup } from '~/Api/User/ProductGroup';
import { invokeWithAccessToken } from '~/AccessToken';
import { ErrorResponse as CzReimbursementErrorResponse } from '~/Api/User/ProductGroup/CzReimbursement';
import { ErrorCode } from 'com.cogvio.pricemonitor.api';
import { postCzMaxPriceExportXls } from '~/libs/infrastructure/api/users/czMaxPrice';
import { ErrorResponse as RoMaxPriceErrorResponse } from '~/Api/User/ProductGroup/RoMaxPrice';
import { ErrorResponse as SkMaxPriceErrorResponse } from '~/Api/User/ProductGroup/SkMaxPrice';
import {
	commitCzReimbursementUpdate,
	createCzReimbursementExportXls,
	handleCzReimbursementUpdateResponse,
	selectExport as selectCzReimbursementExport,
} from '~/CzReimbursement';
import { handleError } from '~/ErrorHandler';
import { flashErrorMessage, removeFlashMessageByText } from '~/FlashMessage';
import { logGoogleTagManagerGroupExport } from '~/GoogleTagManager';
import { createEvidenceExport, createGroupExportXmlUrl } from '~/ProductGroup';
import {
	commitRoMaxPriceUpdate,
	createExport as createRoMaxPriceExport,
	ExportType as RoMaxPriceExportType,
	handleRoMaxPriceUpdateResponse,
	selectExport as selectRoMaxPriceExport,
} from '~/RoMaxPrice';
import {
	commitSkMaxPriceUpdate,
	createSkMaxPriceExportCNRefXls,
	createSkMaxPriceExportCNRefPzluXls,
	createSkMaxPriceExportRawXls,
	handleSkMaxPriceUpdateResponse,
	selectExport as selectSkMaxPriceExport,
} from '~/SkMaxPrice';
import { State, ThunkAction, ThunkDispatch } from '~/Store';
import { formatCountriesError } from '~/UI';
import * as downlaodFlashMessages from './DownloadFlashMessages';

import DownloadPopover from './DownloadPopover';
import { buildErrorLocation, processExport } from './productGroupDownloadHelpers';

const mapStateToProps = (state: State, props: { group: UserProductGroup }) => {
	const { accessToken } = state.accessToken;

	const downloads = accessToken
		? [
				selectSkMaxPriceExport(state.skMaxPrice, props.group.id),
				selectCzReimbursementExport(state.czReimbursement, props.group.id),
				selectRoMaxPriceExport(state.roMaxPrice, props.group.id),
				state.productGroup.exportRequest ? { isFetching: true } : null,
		  ]
		: [];

	return {
		canExportEvidence: accessToken?.subscription?.systemModules.has('product-group-export-evidence') ?? false,
		isFetching: downloads.find((download) => download && download.isFetching) !== undefined,
	};
};

const mapDispatchToProps = (
	dispatch: ThunkDispatch<AnyAction>,
	props: { group: UserProductGroup }
): {
	onProductsExport: (currency: string | null) => Promise<void>;
	onEvidenceExport: () => Promise<void>;
	onCzMaxPriceExport: () => any;
	onCzReimbursementExport: () => any;
	onSkMaxPriceCNRefExport: () => any;
	onSkMaxPriceCNRefPzluExport: () => any;
	onSkMaxPriceRawExport: () => any;
	onRoMaxPriceExport: (type: RoMaxPriceExportType) => Promise<void>;
} => {
	return {
		onProductsExport: async (currency: string | null) => {
			logGoogleTagManagerGroupExport(props.group.id);
			const response = await dispatch(createGroupExportXmlUrl(props.group.id, currency));
			if (response) {
				dispatch(processExport(response.export));
			}
		},
		onEvidenceExport: async () => {
			const response = await dispatch(createEvidenceExport(props.group.id));
			if (response) {
				dispatch(processExport(response.export));
			}
		},
		onCzMaxPriceExport: () => {
			dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingReferenceCountryProductErrorMessage));
			dispatch(removeFlashMessageByText(downlaodFlashMessages.OnlyOneProductPerCountryErrorMessage));
			dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingPackageSizeErrorMessage));
			dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingProductSettingsErrorMessage));

			return dispatch(
				invokeWithAccessToken(async (accessToken) => {
					try {
						const response = await postCzMaxPriceExportXls(accessToken.user.id, props.group.id);
						dispatch(processExport(response.export as unknown as Export));
					} catch (error: any) {
						const errors: Array<{ code: ErrorCode }> = error?.response?.data?.errors ?? [];
						const codes = errors.map((e) => e.code);

						if (codes.includes('czech-max-price-calculation-requires-reference-settings')) {
							dispatch(flashErrorMessage(downlaodFlashMessages.MissingPackageSizeErrorMessage));
						} else if (codes.includes('czech-max-price-calculation-required-product-settings-is-missing')) {
							dispatch(flashErrorMessage(downlaodFlashMessages.MissingProductSettingsErrorMessage));
						} else if (codes.includes('czech-max-price-calculation-requires-at-least-one-reference-country-product')) {
							dispatch(flashErrorMessage(downlaodFlashMessages.MissingReferenceCountryProductErrorMessage));
						} else if (codes.includes('czech-max-price-calculation-requires-at-most-one-product-for-each-country')) {
							dispatch(flashErrorMessage(downlaodFlashMessages.OnlyOneProductPerCountryErrorMessage));
						} else if (codes.includes('czech-max-price-calculation-illegal-price-type-selected')) {
							dispatch(flashErrorMessage(downlaodFlashMessages.IllegalPriceTypeSelectedErrorMessage));
						} else if (
							codes.includes('czech-max-price-calculation-requires-at-least-three-reference-country-products')
						) {
							dispatch(flashErrorMessage(downlaodFlashMessages.MissingThreeReferenceCountryProductsMessage));
						} else {
							dispatch(handleError(error));
						}
					}
				})
			);
		},
		onCzReimbursementExport: () => {
			dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingProductErrorMessage));
			dispatch(removeFlashMessageByText(downlaodFlashMessages.OnlyOneProductPerCountryErrorMessage));
			dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingCzReimbursementReferenceErrorMessage));
			dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingProductSettingsErrorMessage));

			return dispatch(commitCzReimbursementUpdate(props.group.id))
				.then(() => dispatch(createCzReimbursementExportXls(props.group.id)))
				.then((response) => dispatch(processExport(response.export)))
				.catch((response) => {
					if (!(response instanceof CzReimbursementErrorResponse)) {
						dispatch(handleError(response));
						return;
					}

					const validationResult = response.toValidationResult();
					if (validationResult.groupId !== null) {
						dispatch(push(`/product-groups/${validationResult.groupId}`));
					}

					const errorLocation =
						dispatch(buildErrorLocation(validationResult.productErrors)) +
						formatCountriesError(validationResult.countryErrors[0]?.duplicateCountries ?? []);

					if (response.handleCalculationRequiresAtLeastOneReferenceCountryProduct()) {
						dispatch(
							flashErrorMessage(downlaodFlashMessages.MissingReferenceCountryProductErrorMessage + errorLocation)
						);
					}
					if (response.handleCalculationRequiresAtLeastOneProduct()) {
						dispatch(flashErrorMessage(downlaodFlashMessages.MissingProductErrorMessage + errorLocation));
					}
					if (response.handleCalculationRequiresAtMostOneProductForEachCountry()) {
						dispatch(flashErrorMessage(downlaodFlashMessages.OnlyOneProductPerCountryErrorMessage + errorLocation));
					}
					if (response.handleCalculationRequiresReferenceSettings()) {
						dispatch(
							flashErrorMessage(downlaodFlashMessages.MissingCzReimbursementReferenceErrorMessage + errorLocation)
						);
					}
					if (response.handleCalculationRequiredProductSettingsIsMissing()) {
						dispatch(flashErrorMessage(downlaodFlashMessages.MissingProductSettingsErrorMessage + errorLocation));
					}
					if (response.handleIllegalPriceTypeSelected()) {
						dispatch(flashErrorMessage(downlaodFlashMessages.IllegalPriceTypeSelectedErrorMessage + errorLocation));
					}
					if (response.handleCalculationRequiresAtLeastOneProductList()) {
						dispatch(flashErrorMessage(downlaodFlashMessages.MissingProductListErrorMessage + errorLocation));
					}

					dispatch(handleCzReimbursementUpdateResponse(response, props.group.id));
				});
		},
		onSkMaxPriceCNRefExport: () => handleSkMaxPriceExport(createSkMaxPriceExportCNRefXls, dispatch, props),
		onSkMaxPriceCNRefPzluExport: () => handleSkMaxPriceExport(createSkMaxPriceExportCNRefPzluXls, dispatch, props),
		onSkMaxPriceRawExport: () => handleSkMaxPriceExport(createSkMaxPriceExportRawXls, dispatch, props),
		onRoMaxPriceExport: async (type: RoMaxPriceExportType) => {
			dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingProductSettingsErrorMessage));
			dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingReferenceCountryProductErrorMessage));
			dispatch(removeFlashMessageByText(downlaodFlashMessages.OnlyOneProductPerCountryErrorMessage));
			dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingReferenceSettingsErrorMessage));
			dispatch(removeFlashMessageByText(downlaodFlashMessages.RequiresAtMostOneRomanianProductForEachDatasource));

			try {
				await dispatch(commitRoMaxPriceUpdate(props.group.id));
				const response = await dispatch(createRoMaxPriceExport(props.group.id, type));
				dispatch(processExport(response.export));
			} catch (error) {
				if (!(error instanceof RoMaxPriceErrorResponse)) {
					dispatch(handleError(error));
					return;
				}

				const validationResult = error.toValidationResult();
				if (validationResult.groupId !== null) {
					dispatch(push(`/product-groups/${validationResult.groupId}`));
				}

				const errorLocation =
					dispatch(buildErrorLocation(validationResult.productErrors)) +
					formatCountriesError(validationResult.countryErrors[0]?.duplicateCountries ?? []);

				if (error.handleRequiredProductSettingsMissing()) {
					dispatch(flashErrorMessage(downlaodFlashMessages.MissingProductSettingsErrorMessage + errorLocation));
				}
				if (error.handleRequiresMinOneReferenceProduct()) {
					dispatch(flashErrorMessage(downlaodFlashMessages.MissingReferenceCountryProductErrorMessage + errorLocation));
				}
				if (error.handleRequiresMaxOneReferenceProduct()) {
					dispatch(flashErrorMessage(downlaodFlashMessages.OnlyOneProductPerCountryErrorMessage + errorLocation));
				}
				if (error.handleRequiresMaxOneProductPerDataSource()) {
					dispatch(
						flashErrorMessage(downlaodFlashMessages.RequiresAtMostOneRomanianProductForEachDatasource + errorLocation)
					);
				}
				if (error.handleRequiresReferenceSettingsErrorCode()) {
					dispatch(flashErrorMessage(downlaodFlashMessages.MissingReferenceSettingsErrorMessage + errorLocation));
				}
				if (error.handleCalculationRequiresAtLeastOneProductList()) {
					dispatch(flashErrorMessage(downlaodFlashMessages.MissingProductListErrorMessage + errorLocation));
				}
				if (error.handleExportLimitExceeded()) {
					dispatch(flashErrorMessage(downlaodFlashMessages.LimitExceededMessage + errorLocation));
				}
				if (error.handleIllegalPriceTypeSelected()) {
					dispatch(flashErrorMessage(downlaodFlashMessages.IllegalPriceTypeSelectedErrorMessage + errorLocation));
				}
				dispatch(handleRoMaxPriceUpdateResponse(error, props.group.id));
			}
		},
	};
};

function handleSkMaxPriceExport(
	createExport: (groupId: string) => ThunkAction<Promise<any>, AnyAction>,
	dispatch: ThunkDispatch<AnyAction>,
	props: { group: UserProductGroup }
): Promise<any> {
	dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingProductErrorMessage));
	dispatch(removeFlashMessageByText(downlaodFlashMessages.OnlyOneProductPerCountryErrorMessage));
	dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingReferenceSettingsErrorMessage));
	dispatch(removeFlashMessageByText(downlaodFlashMessages.MissingProductSettingsErrorMessage));

	return dispatch(commitSkMaxPriceUpdate(props.group.id))
		.then(() => dispatch(createExport(props.group.id)))
		.then((response) => dispatch(processExport(response.export)))
		.catch((response) => {
			if (!(response instanceof SkMaxPriceErrorResponse)) {
				dispatch(handleError(response));
				return;
			}

			const validationResult = response.toValidationResult();
			if (validationResult.groupId !== null) {
				dispatch(push(`/product-groups/${validationResult.groupId}`));
			}

			const errorLocation =
				dispatch(buildErrorLocation(validationResult.productErrors)) +
				formatCountriesError(validationResult.countryErrors[0]?.duplicateCountries ?? []);

			if (response.handleCalculationRequiresAtLeastOneProduct()) {
				dispatch(flashErrorMessage(downlaodFlashMessages.MissingProductErrorMessage + errorLocation));
			}
			if (response.handleCalculationRequiresAtMostOneProductForEachCountry()) {
				dispatch(flashErrorMessage(downlaodFlashMessages.OnlyOneProductPerCountryErrorMessage + errorLocation));
			}
			if (response.handleCalculationRequiresAtLeastOneNonSkProduct()) {
				dispatch(flashErrorMessage(downlaodFlashMessages.MissingNonSlovakProductErrorMessage + errorLocation));
			}
			if (response.handleCalculationRequiredProductSettingsIsMissing()) {
				dispatch(flashErrorMessage(downlaodFlashMessages.MissingProductSettingsErrorMessage + errorLocation));
			}
			if (response.handleExportRequiresReferenceSettings()) {
				dispatch(flashErrorMessage(downlaodFlashMessages.MissingReferenceSettingsErrorMessage + errorLocation));
			}
			if (response.handleIllegalPriceTypeSelected()) {
				dispatch(flashErrorMessage(downlaodFlashMessages.IllegalPriceTypeSelectedErrorMessage + errorLocation));
			}
			if (response.handleCalculationRequiresAtLeastOneProductList()) {
				dispatch(flashErrorMessage(downlaodFlashMessages.MissingProductListErrorMessage + errorLocation));
			}
			if (response.handleLpCnRefExportContainsSlovakPzluProduct()) {
				dispatch(flashErrorMessage(downlaodFlashMessages.LpCnRefExportContainsSlovakPzluProduct + errorLocation));
			}
			if (response.handlePzluCnRefExportContainsSlovakLpProduct()) {
				dispatch(flashErrorMessage(downlaodFlashMessages.PzluCnRefExportContainsSlovakLpProduct + errorLocation));
			}
			if (response.handleAtLeastOneReferenceCountryProduct()) {
				dispatch(
					flashErrorMessage(downlaodFlashMessages.ExportB1RequiresReferenceCountriesErrorMessage + errorLocation)
				);
			}

			dispatch(handleSkMaxPriceUpdateResponse(response, props.group.id));
		});
}

export default connect(mapStateToProps, mapDispatchToProps)(DownloadPopover);
